 
 
import { Component, Input, input, Output, EventEmitter, signal, WritableSignal, computed, forwardRef, booleanAttribute } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';

@Component({
  selector: 'app-lt-multiselect',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatChipsModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LTMultiSelectComponent),
      multi: true,
    },
  ],
  template: `
    <mat-form-field [appearance]="fillMode" class="full-width">
      @if(label())
      {
        <mat-label>{{label()}}</mat-label>
      }
      <mat-select
        [formControl]="selectControl"
        [required]="required()"
        [placeholder]="placeholder()"
        [compareWith]="compareWith"
        [multiple]="true"
        [panelWidth]="panelWidth()"
        (selectionChange)="onSelectionChange($event)"
        (blur)="onTouched()">
          @for(item of data(); track $index) {
              <mat-option [value]="item[idField()]">
                {{item[displayField()]}}
              </mat-option>
            }
      </mat-select>
    </mat-form-field>
  `,
  styles: `
  mat-form-field {
    width: 100%;
    min-width: 0;
  }
`,
})
export class LTMultiSelectComponent implements ControlValueAccessor {
  //#region Inputs
  @Input() fillMode: 'outline' | 'fill' = 'outline';
  label = input('');
  placeholder = input('Select options');
  textField = input<string>('text');
  valueField = input<string>('value');
  required = input(false, {transform: booleanAttribute});
  panelWidth = input<string | number | null>('auto'); // auto = match trigger width, null / empty string = match longest option width, # = width in px

  //#region Disabled
  @Input({transform: booleanAttribute}) set disabled(isDisabled: boolean) {
    if (isDisabled) {
      this.selectControl.disable();
    }
    else {
      this.selectControl.enable();
    }
  }
  get disabled(): boolean {
    return this.selectControl.disabled;
  }
  //#endregion

  //#region data
  private _data = signal<any[]>([]);
  @Input() set data(value: any[]) {
    this._data.set(value || []);
  }
  get data(): WritableSignal<any[]> {
    return this._data;
  }
  //#endregion

  //#region value
  private _value = signal<any[]>([]);
  // value is the array of selected OBJECTS, the mat-select control holds their ids (option values are item[valueField])
  // Setting the input does not re-emit valueChange, so [value] + (valueChange) bindings cannot loop
  @Input() set value(val: any[]) {
    if (val !== this._value()) {
      this._value.set(val || []);
      this.selectControl.setValue(this.toIds(val), {emitEvent: false});
    }
  }
  get value(): any {
    return this._value();
  }
  //#endregion

  //#endregion Inputs

  // FormControl
  selectControl = new FormControl([]);

  // Output
  @Output() valueChange = new EventEmitter<any[]>();
  @Output() selectionChange = new EventEmitter<any[]>();

  // computed properties
  displayField = computed(() => this.textField());
  idField = computed(() => this.valueField());

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  constructor() {
    this.selectControl.valueChanges.subscribe(ids => {
      const selectedObjects = this.data().filter(item =>
        ids?.includes(item[this.valueField()]),
      );
      this._value.set(selectedObjects);
      this.onChange(selectedObjects);
      this.valueChange.emit(selectedObjects);
    });

  }

  onSelectionChange(event: any): void {
    const selectedObjects = this.data().filter(item =>
      event.value.includes(item[this.valueField()]),
    );
    this._value.set(selectedObjects);
    this.selectionChange.emit(selectedObjects);
  }


  writeValue(values: any[]): void {
    this._value.set(values || []);
    this.selectControl.setValue(this.toIds(values), {emitEvent: false});
  }

  private toIds(values: any[]): any[] {
    return (values || []).map(item => item?.[this.valueField()]);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled){
      this.selectControl.disable();
    }
    else {
      this.selectControl.enable();
    }
  }
  
  // mat-select calls compareWith(optionValue, selectedValue) unbound, and both are ids (option [value] is item[valueField]).
  // Previously a method reading this.valueField(): `this` was the MatSelect (TypeError), and id?.[field] was always
  // undefined === undefined, which would have matched every option.
  compareWith = (id1: any, id2: any): boolean => id1 === id2;
}
